/**
 * G0 (Get Found tactical wire) — the pure "what does the Generate button show
 * right now" state machine, kept framework-free (no React, no fetch) so the
 * idle -> prewarmed -> generating -> served/blocked transitions are
 * unit-testable standalone, the same house idiom presencePrewarmGate.ts /
 * stageMove.ts already establish (decision pure + tested, the component that
 * calls generatePresence is the thin, untested wire).
 *
 * States:
 *   idle        — nothing has fired yet (initial render).
 *   prewarmed   — usePresencePrewarm's mount effect confirmed a fire (or the
 *                 gate resolved false, e.g. cold-start user) — see
 *                 markPrewarmSettled below for why "false" still counts as
 *                 SETTLED, not stuck-loading.
 *   generating  — the user clicked Generate; POST /presence/generate is
 *                 in flight.
 *   served      — the call resolved with a usable profile.
 *   blocked     — the call resolved but the server's own lint gate rejected
 *                 the draft (`blocked: true` + `lintGate`, presence.routes.ts
 *                 L1077) — the R125 "silent nothing" class this lane must not
 *                 regress: a blocked response is NOT the same as served=null,
 *                 it carries a REASON the caller renders.
 *   failed      — the network/API call itself threw (ApiError or a thrown
 *                 Error) — distinct from `blocked` (a real server response
 *                 with a reason) so the two honest-but-different outcomes
 *                 never collapse into one generic "something went wrong."
 *
 * The loading COPY decision (honest, not a fixed "about a minute" unless the
 * measured timings support it) lives in generateLoadingCopy below — see its
 * own doc comment for the R125 timing citation.
 */

export type PresenceGenerateStatus =
  | 'idle'
  | 'prewarmed'
  | 'generating'
  | 'served'
  | 'blocked'
  | 'failed'
  // W23 (docs/WALK-FINDINGS-2026-07-21.md; the owner's word `Go-longops`,
  // 2026-07-24): the client's own bounded status-poll reached its ceiling
  // while the SERVER IS STILL WORKING. This is NOT `failed` — nothing failed.
  // It is the state his "Couldn't generate right now: Load failed" banner was
  // lying about: the server went on to finish that run successfully in 67s.
  // Since the API now banks the finished profile before it answers
  // (composeAndBankPresence), the honest copy for this state can promise the
  // work is kept — see stillWorkingCopy below.
  | 'still-working';

/** The exact subset of POST /presence/generate's response this screen reads
 *  to decide served-vs-blocked, mirrored from presence.routes.ts's own
 *  response shape (L1069-1090) — `blocked`/`lintGate` are ADDITIVE fields
 *  present only when the lint gate failed; a passing generate never carries
 *  them (verified directly against the route, not inferred). */
export interface PresenceGenerateResponseLike {
  profile?: { headline?: string; recommendedTitles?: string[] } | null;
  blocked?: boolean;
  lintGate?: { location: string; kind: string; text: string } | null;
}

export interface PresenceGenerateState {
  status: PresenceGenerateStatus;
  /** Set only in the `blocked` state — the server's own reason, rendered
   *  verbatim, never a client-authored paraphrase (mirrors DeleteRequestResult's
   *  "render the served truth verbatim" law elsewhere in this codebase). */
  blockedReason: { location: string; kind: string; text: string } | null;
  /** Set only in the `failed` state — the thrown error's message, rendered as
   *  the honest "the generate call itself failed" state (distinct from a
   *  blocked lint gate, which is a real response with a reason). */
  errorMessage: string | null;
  /** W23 — the server-side run this screen is watching. Set the moment POST
   *  /presence/generate acks (so a navigation away can resume against it) and
   *  kept through `still-working`; null in every terminal state. */
  runId: string | null;
  /** W23 — true ONLY when this working state was REJOINED from a run recorded
   *  in the session store, never for a fresh click. The two are otherwise
   *  indistinguishable (a fresh run also carries a runId once its ack lands),
   *  and telling a first-time clicker "picking up where we left off" would be
   *  describing something that did not happen to them. */
  resumed: boolean;
}

export const INITIAL_GENERATE_STATE: PresenceGenerateState = {
  status: 'idle',
  blockedReason: null,
  errorMessage: null,
  runId: null,
  resumed: false,
};

/** usePresencePrewarm's mount effect settled — regardless of whether it
 *  actually fired a network call (shouldFirePresencePrewarm may have
 *  resolved false for a cold-start user with no corpus/roles yet). Either
 *  way, "prewarmed" is the correct next state: the screen is no longer
 *  showing its very-first idle paint, and Generate is now a meaningful,
 *  clickable action — never a spinner that never resolves just because a
 *  cold-start user had nothing to warm. */
export function markPrewarmSettled(state: PresenceGenerateState): PresenceGenerateState {
  if (state.status !== 'idle') return state;
  return { status: 'prewarmed', blockedReason: null, errorMessage: null, runId: null, resumed: false };
}

/** The user clicked Generate. Only a legal transition from prewarmed/served/
 *  blocked/failed (never re-entrant from `generating` itself — the caller's
 *  own click-guard should prevent a second click, this is the defensive
 *  backstop matching applyOptimisticStageMove's "one card at a time" posture
 *  for a different pure module). */
export function beginGenerate(state: PresenceGenerateState): PresenceGenerateState {
  if (state.status === 'generating') return state;
  return { status: 'generating', blockedReason: null, errorMessage: null, runId: null, resumed: false };
}

/** W23 — POST /presence/generate acked with a runId. The run now EXISTS
 *  server-side and will finish (and bank its profile) whether or not this
 *  screen is still watching, so the id is carried from here on. */
export function markRunStarted(
  state: PresenceGenerateState,
  runId: string,
): PresenceGenerateState {
  return { ...state, runId };
}

/** W23 — a remount found an in-flight run in the session store (a nav away
 *  and back, or a mobile browser re-launching the SPA shell). Rejoin it:
 *  `generating` with the recovered id, never a second generation. */
export function resumeGenerate(runId: string): PresenceGenerateState {
  return { status: 'generating', blockedReason: null, errorMessage: null, runId, resumed: true };
}

/** W23 — the bounded client-side poll hit its ceiling with the server still
 *  running. NOT a failure: the run continues, and the API banks the profile
 *  before it answers, so nothing is lost. Keeps the runId so the screen (and
 *  the next remount) can rejoin the same run. */
export function markStillWorking(runId: string): PresenceGenerateState {
  return { status: 'still-working', blockedReason: null, errorMessage: null, runId, resumed: false };
}

/** The call resolved. Reads the SAME response shape the real fetch call
 *  returns and decides served vs blocked — the honest branch that must never
 *  regress to the R125 "silent nothing" class (a blocked response rendered as
 *  if nothing happened at all). */
export function resolveGenerateResponse(
  response: PresenceGenerateResponseLike,
): PresenceGenerateState {
  if (response.blocked && response.lintGate) {
    return { status: 'blocked', blockedReason: response.lintGate, errorMessage: null, runId: null, resumed: false };
  }
  return { status: 'served', blockedReason: null, errorMessage: null, runId: null, resumed: false };
}

/** The call itself threw (network failure, non-2xx ApiError, etc.) — distinct
 *  from a blocked response, which is a real server answer with a reason, AND
 *  (W23) distinct from `still-working`, which is not a failure at all. */
export function failGenerate(errorMessage: string): PresenceGenerateState {
  return { status: 'failed', blockedReason: null, errorMessage, runId: null, resumed: false };
}

/** Whether the Generate button should currently be disabled — mid-flight
 *  (including W23's `still-working`, where a server run is genuinely still
 *  going and a second click would start a duplicate the user pays for twice),
 *  or with nothing declared to generate against yet (no roles = the server
 *  would have nothing to bridge; the button stays present but inert rather
 *  than hidden, so the honest empty-state copy pointing at it stays true). */
export function isGenerateDisabled(state: PresenceGenerateState, targetRoleCount: number): boolean {
  return state.status === 'generating' || state.status === 'still-working' || targetRoleCount === 0;
}

/**
 * The honest loading-copy decision. R125's own comment in presence.routes.ts
 * (L364) states the MEASURED timings directly: prewarmed, the wait is the
 * compose engine alone (~30-60s); cold (no prewarm reached), it is
 * bridge+engine (~110s+). Since usePresencePrewarm fires on THIS screen's
 * mount (Profiles.tsx), a user who clicks Generate after even a brief look at
 * the screen has very likely already been prewarmed — "about a minute" is
 * the honest, measured claim for that path. A user who fires Generate before
 * the prewarm could plausibly have settled (state.status === 'idle', i.e.
 * markPrewarmSettled never ran) gets the neutral, unhedged "working…" instead
 * — never a fixed time claim the actual wait might blow past.
 */
export function generateLoadingCopy(prewarmState: PresenceGenerateStatus): string {
  return prewarmState === 'idle' ? 'Working…' : "About a minute — I'm reading your story.";
}

/**
 * W23 — the copy for `still-working`. Every clause here is a fact about what
 * the code actually does, not reassurance:
 *   • "Still writing" — the run is genuinely still open server-side; the
 *     status endpoint said 'running', it did not fail and did not time out.
 *   • "I'll have it saved when it lands" — TRUE as of this lane and not
 *     before: composeAndBankPresence writes the finished profile into
 *     presence_profiles BEFORE the payload leaves the server, so the run
 *     completing is the same event as the work being kept.
 *   • "check your saved versions" — where it will actually appear (the
 *     saved-versions list this screen already renders from GET
 *     /presence/profiles).
 * No stage is claimed ("reading your roles", "polishing") because the code
 * tracks no stages — inventing one would be inventing a fact about his run.
 */
export function stillWorkingCopy(): string {
  return "Still writing — this one's taking longer than a minute. I'll have it saved when it lands, so you can leave this screen and check your saved versions in a bit.";
}

/** W23 — the copy while REJOINING a run a remount recovered (a nav away and
 *  back, or a mobile browser re-launching the app). Says exactly what is
 *  happening: the run was already going, this screen just found it again. */
export function resumingCopy(): string {
  return 'Picking up where we left off — that profile is still writing.';
}
